import { NgClass, NgComponentOutlet, NgStyle } from '@angular/common';
import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    computed,
    HostListener,
    OnDestroy,
    signal,
    Type,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { TranslocoDirective } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import {
    FuseHorizontalNavigationComponent,
    FuseVerticalNavigationComponent,
} from '../../../../@fuse/components/navigation';
import { AuthService } from '../../../core/auth/auth.service';
import { BrandsService } from '../../../core/brands/brands.service';
import { NavigationService } from '../../../core/navigation/navigation.service';
import { Navigation } from '../../../core/navigation/navigation.types';
import { UserService } from '../../../core/user/user.service';
import { User } from '../../../core/user/user.types';
import { AccountPageComponent } from '../../../modules/fidelity-app/account/account-page.component';
import { SignInPageComponent } from '../../../modules/fidelity-app/auth/sign-in/sign-in-page.component';
import { tbRouteAnimations, tbSlideInOut } from '../../../shared/animations/tb-animations.consts';
import { FidelityBasePageComponent } from '../../../shared/components/fidelity-base-page/fidelity-base-page.component';
import { GenericPageLoadingStateComponent } from '../../../shared/components/generic-page-loading-state/generic-page-loading-state.component';

@Component({
    selector: 'fidelity-app-layout',
    templateUrl: './fidelity-app.component.html',
    styleUrls: ['./fidelity-app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        RouterOutlet,
        TranslocoDirective,
        FuseHorizontalNavigationComponent,
        MatIcon,
        MatRipple,
        MatButton,
        FuseVerticalNavigationComponent,
        NgComponentOutlet,
        NgClass,
        NgStyle,
        GenericPageLoadingStateComponent,
    ],
    animations: [tbRouteAnimations, tbSlideInOut],
})
export class FidelityAppLayoutComponent extends FidelityBasePageComponent implements OnDestroy, AfterViewChecked {
    // -----------------------------------------------------------------------------------------------------
    // @ Properties
    // -----------------------------------------------------------------------------------------------------

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    layoutHeight = signal<string>(null);
    deferredPrompt = signal<any>(null);
    fidelityAppNav: Navigation;
    currentUser: User = null;

    showInstallBtn = computed<boolean>(() => {
        if (this.displayIsStandalone() == null) {
            return false;
        }

        // Show install button if not in standalone mode
        let showBtn = !this.displayIsStandalone();
        // Additionally, check if the current browser is Chrome or Edge (i.e. the ones that support the install prompt)
        const isChromeOrEdge = /chrome|edge/i.test(navigator.userAgent);
        // If so, add the presence of a defferred prompt to the condition
        if (isChromeOrEdge) {
            showBtn = showBtn && this.deferredPrompt() != null;
        }
        return showBtn;
    });
    showInstallOverlay = signal<boolean>(false);
    isIOS = signal<boolean>(false);
    displayIsStandalone = signal<boolean>(false);

    availablePages = {
        profile: null,
    };

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    private refreshLayoutHeight: Function = () => {
        //Check if the device is a mobile device
        const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (this.fidelityAppNavService.deviceIsMobile || hasTouch) {
            return;
        }
        this.layoutHeight.set(window.visualViewport.height + 'px');
    };

    /**
     * Prevents an "expression changed after check" network-error caused by the use of animations
     * https://github.com/angular/angular/issues/36173#issuecomment-681282309
     */
    ngAfterViewChecked(): void {
        this.changeDetectorRef.detectChanges();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Event listeners
    // -----------------------------------------------------------------------------------------------------
    @HostListener('window:popstate', ['$event'])
    onPopState(_: any) {
        if (this.fidelityAppNavService.deviceIsMobile && this.fidelityAppNavService.pagesHistory.length > 0) {
            this.fidelityAppNavService.back(true);
        }
    }

    @HostListener('window:beforeinstallprompt', ['$event'])
    onBeforeInstallPrompt($event: any) {
        // Prevent Chrome 67 and earlier from automatically showing the prompt
        $event.preventDefault();
        // Stash the event so it can be triggered later.
        this.deferredPrompt.set($event);
    }

    /**
     * Constructor
     */
    constructor(
        protected outlet: RouterOutlet,
        protected router: Router,
        protected brandsService: BrandsService,
        private changeDetectorRef: ChangeDetectorRef,
        private authService: AuthService,
        private userService: UserService,
        private _navigationService: NavigationService
    ) {
        super();

        // Subscribe to navigation data
        this._navigationService.navigation$.pipe(takeUntilDestroyed()).subscribe((navigation: Navigation) => {
            this.fidelityAppNav = navigation;
        });

        // Detect iOS devices
        this.isIOS.set(this.detectIOS());

        this.userService.user$.subscribe((user) => {
            this.currentUser = user;
            this.availablePages = { profile: user ? AccountPageComponent : SignInPageComponent };
        });

        this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
            if (event instanceof NavigationStart && this.fidelityAppNavService.pagesHistory.length > 0) {
                setTimeout(() => this.fidelityAppNavService.closePageAndClearHistory());
            }
        });

        this.authService.check().subscribe((authenticated) => {
            this.availablePages = { profile: authenticated ? AccountPageComponent : SignInPageComponent };
        });

        this.displayIsStandalone.set(window.matchMedia('(display-mode: standalone)').matches);
        this.layoutHeight.set(window.visualViewport.height + 'px');
        window.onresize = this.refreshLayoutHeight.bind(this);
    }

    prepareRoute(outlet: RouterOutlet) {
        return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
    }

    openPageDesktop(page: Type<any>) {
        //When opening the profile/login page from desktop, we need to check if the nav already opened
        //a subtree that contain such pages. in these cases, we shall not add yet another
        //layer of the same page that is already opened in the background
        if (this.fidelityAppNavService.pagesHistory.find((x) => x.component == page) != null) {
            return;
        }
        this.fidelityAppNavService.closePageAndClearHistory();
        setTimeout(() => super.openPage(page));
    }

    /**
     * Close the install overlay
     */
    closeInstallOverlay() {
        this.showInstallOverlay.set(false);
    }

    /**
     * Add the app to home screen
     */
    addToHomeScreen() {
        // If we have the deferredPrompt, use it
        if (this.deferredPrompt()) {
            // hide our user interface that shows our A2HS button
            // show the prompt
            this.deferredPrompt().prompt();
            // Wait for the user to respond to the prompt
            this.deferredPrompt().userChoice.then((choiceResult: any) => {
                if (choiceResult.outcome === 'accepted') {
                    this.deferredPrompt.set(null);
                }
            });
        } else {
            // If we don't have the deferredPrompt, show the overlay
            this.showInstallOverlay.set(true);
        }
    }

    /**
     * Detect if the device is running iOS
     */
    private detectIOS(): boolean {
        const userAgent = window.navigator.userAgent.toLowerCase();
        return /iphone|ipad/.test(userAgent);
    }
}
